/**
 * Bruit procédural déterministe, sans dépendance ni table pré-calculée.
 *
 * Le bruit est une **fonction pure** de ses coordonnées et d'une graine : aucun
 * état, aucun générateur à faire avancer. C'est ce qui permet de générer un
 * morceau de monde à la demande, dans n'importe quel ordre, et de retrouver
 * exactement le même relief en revenant sur ses pas — là où un `Math.random()`
 * séquentiel donnerait un terrain différent à chaque visite.
 */

/**
 * Constantes de mélange empruntées à MurmurHash3 / xxHash. Toute l'arithmétique
 * passe par `Math.imul` et des décalages non signés pour rester en entiers
 * 32 bits : au-delà, les flottants JS perdent les bits de poids faible et le
 * hachage se met à corréler — le terrain montrerait alors des motifs répétés.
 */
function hash2(ix: number, iz: number, seed: number): number {
  let h = Math.imul(ix, 0x27d4eb2d) ^ Math.imul(iz, 0x165667b1) ^ Math.imul(seed, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;

  return (h >>> 0) / 0x100000000;
}

/**
 * Courbe d'interpolation quintique `6t⁵ − 15t⁴ + 10t³`.
 *
 * Ses dérivées première **et** seconde s'annulent en 0 et en 1, donc la pente du
 * terrain est continue au passage d'une cellule à l'autre. Une interpolation
 * linéaire, ou même la cubique `3t² − 2t³`, laisserait une cassure de pente que
 * l'éclairage révèle sous forme de réseau de plis réguliers.
 */
function quintic(t: number): number {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/**
 * Bruit de valeur 2D. Résultat **strictement dans `[-1, 1]`** : les quatre
 * valeurs hachées sont dans `[0, 1)` et l'interpolation bilinéaire d'une
 * combinaison convexe ne peut pas sortir de leur intervalle.
 */
export function valueNoise2D(x: number, z: number, seed: number): number {
  const ix = Math.floor(x);
  const iz = Math.floor(z);
  const fx = quintic(x - ix);
  const fz = quintic(z - iz);

  const n00 = hash2(ix, iz, seed);
  const n10 = hash2(ix + 1, iz, seed);
  const n01 = hash2(ix, iz + 1, seed);
  const n11 = hash2(ix + 1, iz + 1, seed);

  const nx0 = n00 + (n10 - n00) * fx;
  const nx1 = n01 + (n11 - n01) * fx;

  return (nx0 + (nx1 - nx0) * fz) * 2 - 1;
}

/**
 * Rotation appliquée au repère entre deux octaves (≈ 31°).
 *
 * Sans elle, toutes les octaves s'alignent sur le même réseau carré et leurs
 * creux se superposent : un quadrillage régulier apparaît, très visible sur un
 * sol dégagé. Un angle non remarquable évite que le motif ne se referme au bout
 * de quelques octaves.
 */
const OCTAVE_ROTATION = 0.54;
const ROTATION_COS = Math.cos(OCTAVE_ROTATION);
const ROTATION_SIN = Math.sin(OCTAVE_ROTATION);

/**
 * Écart de graine entre deux octaves. Sans lui, les octaves seraient le même
 * bruit à des échelles différentes — visuellement, un relief auto-similaire.
 */
const OCTAVE_SEED_STRIDE = 1013;

export interface FbmOptions {
  /** Graine du monde : deux graines différentes donnent deux reliefs différents. */
  readonly seed: number;
  /** Nombre d'octaves cumulées. Chacune ajoute un niveau de détail plus fin. */
  readonly octaves: number;
  /** Facteur de fréquence d'une octave à la suivante (2 = une octave musicale). */
  readonly lacunarity: number;
  /** Facteur d'amplitude d'une octave à la suivante (0,5 = deux fois moins haut). */
  readonly persistence: number;
}

/**
 * Somme d'octaves de bruit (*fractal Brownian motion*), **normalisée**.
 *
 * La division finale par la somme des amplitudes est ce qui borne le résultat :
 * chaque octave vaut au plus 1 en valeur absolue, donc `|somme| ≤ Σ amplitudes`,
 * et le quotient reste dans `[-1, 1]` quels que soient les réglages. C'est une
 * garantie *par construction*, pas un `clamp` appliqué après coup — un écrêtage
 * transformerait les sommets en plateaux et les creux en cuvettes plates.
 */
export function fbm2D(x: number, z: number, options: FbmOptions): number {
  const { seed, octaves, lacunarity, persistence } = options;

  let sum = 0;
  let normalizer = 0;
  let amplitude = 1;
  let sampleX = x;
  let sampleZ = z;

  for (let octave = 0; octave < octaves; octave++) {
    sum += valueNoise2D(sampleX, sampleZ, seed + octave * OCTAVE_SEED_STRIDE) * amplitude;
    normalizer += amplitude;
    amplitude *= persistence;

    // Octave suivante : on resserre le repère, puis on le fait tourner.
    const nextX = sampleX * lacunarity;
    const nextZ = sampleZ * lacunarity;
    sampleX = nextX * ROTATION_COS - nextZ * ROTATION_SIN;
    sampleZ = nextX * ROTATION_SIN + nextZ * ROTATION_COS;
  }

  // `octaves: 0` demande un terrain sans relief : renvoyer 0 plutôt qu'un NaN.
  return normalizer === 0 ? 0 : sum / normalizer;
}
